import axios from 'axios';
import { CONST } from 'constants/enums';
import qs from 'qs';
import { includes } from 'ramda';
import agreements, { url_agreements } from 'utils/api/agreements';
import authentication, { signOut } from 'utils/api/authentication';
import commons, { url_commons } from 'utils/api/commons';
import companies, { url_companies } from 'utils/api/companies';
import invoices from 'utils/api/invoices';
import reports, { url_reports } from 'utils/api/reports';
import sales from 'utils/api/sales';
import store, { url_store } from 'utils/api/store';
import transfers from 'utils/api/transfers';
import user, { url_users } from 'utils/api/user';
import { PERM, PERM_CONTEXT } from 'utils/can';
import { to } from 'utils/helper';
import { getAccessToken, getRefreshToken, getStorageLanguage } from 'utils/hooks/storage/storageGets';
import {setUserAuth } from 'utils/hooks/storage/storageSets';
import { getPermContext, getSlugAgreement } from 'utils/hooks/usePermContext';

import dashboard from './dashboard';

const apiUrl = process.env.GATSBY_BACKEND_URL;

export const service = axios.create({
	baseURL: apiUrl,
	headers: {
		'Content-Type': 'application/json',
	}
});

service.interceptors.request.use(config => {
	const accessToken = getAccessToken();
	const language = getStorageLanguage();
	
	const headers = Object.assign(config.headers,
		accessToken && { Authorization: `Bearer ${accessToken}` },
		{ 'Accept-Language': language },
	);

	// TODO: path with multiple slugs or slug inside
	// all urls where we do not add a "om" and "agreement" parameter
	const pathFuncArray = [
		url_store.update_store_order(),
		url_store.store_order(),
		url_commons.note(),
	].map(url => config.url.includes(url));

	const isUrlFound = includes(true, pathFuncArray);

	// TODO: refactor this? PERM.IS_ADMIN() probably undefined here
	const shouldAddOmContext = PERM.IS_ADMIN() && !isUrlFound && !includes(config.url, [
		url_users.auth_token,
		url_users.revoke_token,
		url_users.supplier,
		url_users.reset_pin_step1,
		url_users.reset_pin_step2,
		url_users.reset_pin_step3,
		url_users.reset_pin_step4,
		url_users.reset_pin_step5,
		url_users.send_personal_suppliers_mail,
		url_users.send_suppliers_mail,
		url_users.suppliers,
		url_users.send_message,
		url_users.data,
		url_users.auth_change_password,
		url_users.update,
		url_users.set_agreement,
		url_store.products,
		url_store.create_store_order,
		url_store.change_status,
		url_store.store_orders,
		url_commons.web_static_data,
		url_commons.notes,
		url_commons.compliance_finteq_privacy_policy,
		url_agreements.list,
		url_companies.list,
		url_companies.suppliers
	]);

	const context = getPermContext(window.location.pathname);
	const agreementSlug = getSlugAgreement(window.location.pathname, context);

	// all urls where we do not add a "agreement" parameter
	const pathFuncArrayForAgreement = [
		url_reports.settlements_with_suppliers(),
		url_reports.agreement_slug_animator_payments(),
		url_reports.agreement_slug_cashback(),
		url_reports.agreement_slug_invoices_with_orders(),
	].map(url => config.url.includes(url));

	const isUrlFoundForAgreement = includes(true, pathFuncArrayForAgreement);
	const shouldAddAgreement = !isUrlFoundForAgreement && includes(context, [PERM_CONTEXT.ADX, PERM_CONTEXT.UAX, PERM_CONTEXT.UAXB, PERM_CONTEXT.KAP]);

	const params = Object.assign(config.params ?? {},
		shouldAddOmContext && { om: context},
		shouldAddOmContext && shouldAddAgreement && agreementSlug && { agreement: agreementSlug },
	);

	return ({
		...config,
		headers,
		params
	});
});

service.interceptors.response.use((response) => {
	return response;
}, async function (error) {
	const originalRequest = error.config;
	const language = getStorageLanguage();
	const refreshToken = getRefreshToken();

	if (!refreshToken) {
		signOut();
		return Promise.reject(error);
	} else if (error?.response?.status === CONST.RESPONSE.STATUS_401 && !originalRequest._retry) {
		const [error, response] = await to(service.post(url_users.auth_token,
			qs.stringify({
				refresh_token: refreshToken,
				grant_type: 'refresh_token',
				client_id: process.env.GATSBY_CLIENT_ID
			}),
			{
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded'
				}
			}
		));

		if (error) {
			signOut();
			return Promise.reject(error);
		} else {
			setUserAuth(response);
			axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
			axios.defaults.headers.common['Accept-Language'] = language;
			originalRequest._retry = true;
		}
		return service(originalRequest);
	}
	return Promise.reject(error);
});

const api = {
	agreements: agreements(service),
	authentication: authentication(service),
	invoices: invoices(service),
	companies: companies(service),
	commons: commons(service),
	dashboard: dashboard(service),
	user: user(service),
	store: store(service),
	transfers: transfers(service),
	reports: reports(service),
	sales: sales(service)
};

export default api;
